import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, formatApiError } from "@/auth";
import { LHSLogo } from "@/logos";
import { Loader2, KeyRound, Mail, ShieldCheck } from "lucide-react";

export default function ForgotPasswordPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [linkVerified, setLinkVerified] = useState(false);

  // If arriving via ?token=… (double-validation link from email), pre-fill & skip step 1
  useEffect(() => {
    const token = params.get("token");
    const em = params.get("email");
    if (!token || !em) return;
    (async () => {
      try {
        const { data } = await axios.post(`${API}/auth/verify-link`, { token });
        if (data.kind === "reset") {
          setEmail(data.email || em);
          setLinkVerified(true);
          setStep(2);
          setMsg({ ok: true, text: "Lien vérifié. Saisissez le code OTP reçu et votre nouveau mot de passe." });
        }
      } catch (e) {
        setMsg({ ok: false, text: e.response?.data?.detail || "Lien invalide ou expiré" });
      }
    })();
  }, []); // eslint-disable-line

  const requestCode = async (e) => {
    e.preventDefault(); setMsg(null); setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setMsg({ ok: true, text: "Si le compte existe, un code a été envoyé à " + email });
      setStep(2);
    } catch (er) { setMsg({ ok: false, text: formatApiError(er) }); }
    finally { setLoading(false); }
  };

  const doReset = async (e) => {
    e.preventDefault(); setMsg(null); setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { email, code, new_password: newPassword });
      setMsg({ ok: true, text: "Mot de passe réinitialisé. Vous pouvez vous connecter." });
      setTimeout(() => nav("/login"), 1400);
    } catch (er) { setMsg({ ok: false, text: formatApiError(er) }); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-black bg-grid flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><LHSLogo size={64} /></div>
        <div className="card-lhs p-6 sm:p-8" data-testid="forgot-card">
          <h1 className="font-display text-2xl font-extrabold uppercase tracking-wider mb-1">Mot de passe oublié</h1>
          <p className="text-sm text-zinc-400 mb-6">Réinitialisez votre mot de passe par e-mail</p>

          {linkVerified && (
            <div className="mb-4 flex items-center gap-2 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2" data-testid="link-verified">
              <ShieldCheck className="w-4 h-4" /> Lien de réinitialisation vérifié pour {email}
            </div>
          )}

          {step === 1 && (
            <form onSubmit={requestCode} className="space-y-3">
              <input data-testid="forgot-email" required type="email" className="input-lhs" placeholder="Votre adresse e-mail"
                value={email} onChange={(e) => setEmail(e.target.value)} />
              <button data-testid="forgot-submit" disabled={loading} className="btn-orange w-full rounded-lg py-3 flex items-center justify-center gap-2 uppercase tracking-wider text-sm">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Recevoir un code
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={doReset} className="space-y-3">
              <input data-testid="reset-code" required maxLength={6} pattern="[0-9]{6}" inputMode="numeric"
                className="input-lhs font-mono-tech text-xl text-center tracking-[.4em]"
                placeholder="••••••" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
              <input data-testid="reset-new" required type="password" minLength={6} className="input-lhs"
                placeholder="Nouveau mot de passe (6+)" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
              <button data-testid="reset-submit" disabled={loading} className="btn-orange w-full rounded-lg py-3 flex items-center justify-center gap-2 uppercase tracking-wider text-sm">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Réinitialiser
              </button>
            </form>
          )}

          {msg && (
            <div className={`mt-4 text-sm px-3 py-2 rounded-lg border ${msg.ok ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" : "text-red-400 border-red-500/40 bg-red-500/10"}`}>
              {msg.text}
            </div>
          )}

          <div className="mt-6 pt-5 border-t border-zinc-800 text-center">
            <Link data-testid="link-login" to="/login" className="text-sm text-zinc-400 hover:text-orange-500">Retour à la <span className="text-orange-500 font-semibold">connexion</span></Link>
          </div>
        </div>
      </div>
    </div>
  );
}
